import type { AgentOptions } from "@newrelic/browser-agent/loaders/agent";
import type { AppMountContext } from "@tc-superpods/app-contract";
import { newRelicConfig, userpilotConfig, type UserpilotConfig } from "./shell/config.js";
import type { TenantSummary } from "./shell/types.js";

export type UserpilotIdentity = Pick<AppMountContext["identity"], "userId" | "name" | "email">;
export type UserpilotTenant = Pick<TenantSummary, "id" | "name">;

type UserpilotSdk = {
  initialize: (token: string) => void;
  identify: (userId: string, properties?: Record<string, unknown>) => void;
  reload: () => void;
};

type UserpilotModule = {
  Userpilot: UserpilotSdk;
};

type UserpilotLoader = () => Promise<UserpilotModule>;

export type UserpilotClient = Readonly<{
  initialize: () => Promise<void>;
  identify: (identity: UserpilotIdentity, tenant?: UserpilotTenant) => Promise<void>;
  reload: () => Promise<void>;
}>;

export function createUserpilotClient(
  config: UserpilotConfig | undefined,
  load: UserpilotLoader
): UserpilotClient {
  let modulePromise: Promise<UserpilotModule> | null = null;
  let initialized = false;

  const getSdk = async (): Promise<UserpilotSdk | null> => {
    if (!config) {
      return null;
    }

    const module = await (modulePromise ??= load());
    if (!initialized) {
      module.Userpilot.initialize(config.token);
      initialized = true;
    }

    return module.Userpilot;
  };

  return {
    initialize: async () => {
      await getSdk();
    },
    identify: async (identity, tenant) => {
      const sdk = await getSdk();
      if (!sdk) {
        return;
      }

      const properties: Record<string, unknown> = {
        name: identity.name,
        email: identity.email
      };
      if (tenant) {
        properties.company = {
          id: tenant.id,
          name: tenant.name
        };
      }

      sdk.identify(identity.userId, properties);
    },
    reload: async () => {
      const sdk = await getSdk();
      sdk?.reload();
    }
  };
}

const userpilotClient = createUserpilotClient(
  userpilotConfig,
  () => import("userpilot") as Promise<UserpilotModule>
);

export const initializeUserpilot = userpilotClient.initialize;
export const identifyUserpilot = userpilotClient.identify;
export const reloadUserpilot = userpilotClient.reload;

export async function initializeNewRelic(): Promise<void> {
  if (!newRelicConfig) return;

  const { BrowserAgent } = await import("@newrelic/browser-agent/loaders/browser-agent");
  const options = {
    info: {
      applicationID: newRelicConfig.applicationId,
      beacon: newRelicConfig.beacon,
      errorBeacon: newRelicConfig.errorBeacon,
      licenseKey: newRelicConfig.licenseKey,
      sa: 1
    },
    init: {
      ajax: {
        deny_list: [newRelicConfig.beacon]
      },
      browser_consent_mode: {
        enabled: false
      },
      distributed_tracing: {
        enabled: true
      },
      performance: {
        capture_detail: false,
        capture_marks: false,
        capture_measures: true
      },
      privacy: {
        cookies_enabled: true
      }
    },
    loader_config: {
      accountID: newRelicConfig.accountId,
      agentID: newRelicConfig.agentId,
      applicationID: newRelicConfig.applicationId,
      licenseKey: newRelicConfig.licenseKey,
      trustKey: newRelicConfig.trustKey
    }
  } satisfies AgentOptions;

  new BrowserAgent(options);

  console.log("New Relic initialized for environment with application ID: " + newRelicConfig.applicationId);
}
